import { ActionIcon, Drawer, useComputedColorScheme } from '@mantine/core';
import { IconMaximize, IconX } from '@tabler/icons-react';
import { useResetAtom } from 'jotai/utils';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { screenshotDiffAtom } from '~/atoms/atoms';
import { useProjectId } from '~/hooks/context/useProjectId';
import { ZIndexProvider } from '~/hooks/context/useZIndex';
import TestRunContent from './TestRunContent';
import TestRunHeader from './TestRunHeader';

const DRAWER_Z_INDEX = 300;

export default function TestRunDrawerView({
  runId,
  opened,
  onClose,
  initialTab = 'replay',
}: {
  runId?: string;
  opened: boolean;
  onClose: () => void;
  initialTab?: string;
}) {
  const projectId = useProjectId();
  const router = useRouter();
  const resetScreenshotDiff = useResetAtom(screenshotDiffAtom);
  const colorScheme = useComputedColorScheme();
  const iconColor = colorScheme === 'dark' ? '#fff' : '#555';

  const { attemptId } = router.query;
  // Get the run or suite ID from URL query parameters if available, otherwise use current runId
  const runOrSuiteId =
    router.query.runOrSuiteId || (router.query.testSuiteRunId ? `g_${router.query.testSuiteRunId}` : runId);

  // Get the current tab and attemptId from the URL
  const tab = initialTab || router.query.tab || 'diffView';

  const fullPageViewUrl = `/project/${projectId}/history/${runOrSuiteId}/run/${runId}?tab=${tab}${attemptId ? `&attemptId=${attemptId}` : ''}`;

  useEffect(() => {
    return () => {
      resetScreenshotDiff();
    };
  }, [resetScreenshotDiff]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={null}
      position="right"
      size="93%"
      zIndex={DRAWER_Z_INDEX}
      withCloseButton={false}
      classNames={{
        body: 'p-0 h-full',
        root: `z-[${DRAWER_Z_INDEX}]`,
        content: 'overflow-y-auto',
      }}
    >
      <ZIndexProvider zIndex={DRAWER_Z_INDEX}>
        <div className="flex h-full flex-col px-4">
          <div className="flex items-center gap-2 border-b py-2 dark:border-zinc-700">
            <ActionIcon
              onClick={onClose}
              variant="transparent"
              aria-label="Close drawer"
            >
              <IconX
                size={18}
                stroke={1.5}
                color={iconColor}
              />
            </ActionIcon>
            <ActionIcon
              variant="transparent"
              aria-label="Full page view"
              component={Link}
              href={fullPageViewUrl}
            >
              <IconMaximize
                size={18}
                stroke={1.5}
                color={iconColor}
              />
            </ActionIcon>
          </div>
          <div className="flex flex-1 flex-col pt-6">
            {!!runId && (
              <>
                <TestRunHeader runId={runId} />
                <TestRunContent
                  runId={runId}
                  isFullView={false}
                  className="h-full pt-3"
                  initialTab={initialTab}
                />
              </>
            )}
          </div>
        </div>
      </ZIndexProvider>
    </Drawer>
  );
}
